import { Link } from 'react-router-dom';
import { t } from '@app/i18n';
import { dataHour } from '@app/format';
import { workflowLink } from '../ApprovalWorkflow/ApprovalWorkflowModel';
import type { ApprovalEvidence as Evidence, WithApprovalEvidence } from './ApprovalEvidenceModel';
import s from './Approvals.module.css';

export function evidenceExplanation(evidence: Evidence): string {
  const environment = evidence.environment?.name ?? evidence.environment?.slug ?? '';
  const required = evidence.required_by;
  if (!evidence.automatic && required?.reason === 'environment_rule' && required.event) {
    const event = t(`approvalWorkflow.event.${required.event}`);
    return t(
      required.class ? 'approvals.evidence.requiredClass' : 'approvals.evidence.requiredEvent',
      {
        environment,
        event,
        class: required.class ?? '',
      },
    );
  }
  if (
    evidence.automatic &&
    ['environment_exempt', 'capability_removed'].includes(evidence.cause) &&
    environment
  ) {
    const events = [...new Set(evidence.changes.map((change) => change.event))]
      .map((event) => t(`approvalWorkflow.event.${event}`))
      .join(' · ');
    return t('approvals.evidence.exempt', { environment, events });
  }
  switch (evidence.cause) {
    case 'no_environment':
      return t('approvals.evidence.noEnvironment');
    case 'environment_not_selectable':
      return t('approvals.evidence.notSelectable');
    case 'unknown_criticality':
      return t('approvals.evidence.unknown');
    case 'candidate_requires_review':
      return t('approvals.evidence.candidate');
    case 'catalog_unavailable':
      return t('approvals.evidence.catalog');
    case 'code_only':
      return t('approvals.evidence.codeOnly');
    case 'already_covered':
      return t('approvals.evidence.covered');
    case 'superseded':
      return t('approvals.evidence.superseded');
    case 'resource_removed':
      return t('approvals.evidence.removed');
    default:
      return t('approvals.evidence.unavailable');
  }
}

/** A recorded exemption is not successful issuance. Status and failure stay separate. */
export function ApprovalEvidence({
  approval,
  detailed = false,
}: {
  approval: WithApprovalEvidence & { status: string; auto_failed_code?: string | null };
  detailed?: boolean;
}) {
  const evidence = approval.decision_evidence;
  if (!evidence || evidence.version !== 1)
    return <span className={s.muted}>{t('approvals.evidence.unavailable')}</span>;
  const environment = evidence.environment;
  const ruleCause = ['environment_rule', 'environment_exempt', 'capability_removed'].includes(
    evidence.cause,
  );
  return (
    <div>
      <span>{evidenceExplanation(evidence)}</span>
      {approval.status === 'pendente' && evidence.automatic ? (
        <p>
          {t(
            approval.auto_failed_code
              ? 'approvals.evidence.deliveryFailed'
              : 'approvals.evidence.awaitingDelivery',
          )}
        </p>
      ) : null}
      {detailed ? (
        <p className={s.muted}>
          {t('approvals.evidence.evaluatedAt', {
            at: dataHour(evidence.evaluated_at) ?? evidence.evaluated_at,
          })}
        </p>
      ) : null}
      {detailed && environment && ruleCause ? (
        <Link to={workflowLink(environment.id)}>
          {t('approvals.evidence.openRules', { environment: environment.name })}
        </Link>
      ) : null}
    </div>
  );
}
